
import Layout from "@/components/layout/Layout";
import { AccountHeader } from "@/components/account/AccountHeader";
import { ProfileSection } from "@/components/account/ProfileSection";
import { ReadingHistory } from "@/components/account/ReadingHistory";
import { SavedArticles } from "@/components/account/SavedArticles";
import { DownloadHistory } from "@/components/account/DownloadHistory";
import { NotificationSettings } from "@/components/account/NotificationSettings";
import { SubscriptionSection } from "@/components/account/SubscriptionSection";
import { AccountSidebar } from "@/components/account/AccountSidebar";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, Shield } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";

const readingHistory = [
  {
    id: 1,
    title: "Understanding Large Language Models",
    date: "Apr 15, 2025",
    readTime: "8 min read",
  },
  {
    id: 2,
    title: "The Future of Generative AI in Enterprise",
    date: "Apr 12, 2025",
    readTime: "12 min read",
  },
  {
    id: 3,
    title: "AI Ethics: Navigating the Challenges",
    date: "Apr 10, 2025",
    readTime: "10 min read",
  },
];

const downloads = [
  {
    id: 1,
    title: "AI Implementation Guide",
    type: "PDF",
    size: "2.4 MB",
    date: "Apr 14, 2025",
  },
  {
    id: 2,
    title: "Machine Learning Cheat Sheet",
    type: "PDF",
    size: "1.8 MB",
    date: "Apr 08, 2025",
  },
];

const sectionComponents: Record<string, React.ReactNode> = {
  profile: <ProfileSection />,
  subscription: <SubscriptionSection />, // Updated: removed the user prop
  content: (
    <div className="grid gap-8 md:grid-cols-2">
      <ReadingHistory items={readingHistory} />
      <SavedArticles />
      <DownloadHistory downloads={downloads} />
    </div>
  ),
  notifications: <NotificationSettings />,
  admin: (
    <div className="py-8">
      <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
        <Shield className="text-ai-indigo mb-2 w-10 h-10" />
        <h2 className="text-xl font-semibold mb-2">Admin Dashboard</h2>
        <p className="text-gray-600 mb-4 text-center max-w-lg">Access powerful admin tools and manage your site here.</p>
        <a
          href="/admin"
          className="bg-ai-indigo text-white px-6 py-2 rounded hover:bg-ai-indigo/90 transition"
        >
          Go to Admin Dashboard
        </a>
      </div>
    </div>
  ),
};

const UserAccount = () => {
  const [selectedSection, setSelectedSection] = useState("profile");
  const { user } = useAuth();
  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user?.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (selectedSection === "admin" && user?.email !== "larsheimsath@hotmail.com") {
      setSelectedSection("profile");
    }
  }, [selectedSection, user?.email]);

  return (
    <Layout>
      <div className="bg-gray-50 py-8 min-h-[80vh]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:space-x-8 px-4 sm:px-6 lg:px-8">
          <div className="mb-8 md:mb-0">
            <AccountSidebar
              selected={selectedSection}
              onSelect={setSelectedSection}
            />
          </div>
          <div className="flex-1">
            {isLoading ? (
              <div className="flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <>
                <AccountHeader
                  user={{
                    name: profile?.full_name || "User Profile",
                    avatar: profile?.avatar_url || "",
                    subscription: "basic",
                    memberSince: "January 10, 2025",
                    id: user?.id,
                  }}
                />
                <div>
                  {sectionComponents[selectedSection]}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserAccount;
